/**
 * EnhancedErrorBoundary - Erweiterte Fehlerbehandlung mit Debug-Informationen
 * 
 * Zeigt detaillierte technische Informationen für Debugging:
 * - Browser/User-Agent
 * - Aktuelle Route
 * - Build-Version/Hash
 * - Cache-Status
 */
import React from 'react';

// Build-Version aus Environment oder Fallback
const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || 
  `build-${new Date().toISOString().split('T')[0]}`;

class EnhancedErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      debugInfo: null,
      autoRecoveryAttempted: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorMessage = error?.message || '';
    
    // SPEZIAL-BEHANDLUNG für insertBefore Fehler
    // Diese sind oft transient und können durch Reload behoben werden
    const isInsertBeforeError = 
      errorMessage.includes('insertBefore') || 
      errorMessage.includes('removeChild') ||
      errorMessage.includes('NotFoundError');
    
    if (isInsertBeforeError && !this.state.autoRecoveryAttempted) {
      console.warn('[ErrorBoundary] insertBefore error detected - attempting auto-recovery...');
      
      this.setState({ autoRecoveryAttempted: true });
      
      // Automatische Wiederherstellung versuchen
      setTimeout(() => {
        // Cache löschen und reload
        this.performAutoRecovery();
      }, 500);
      
      return;
    }
    
    // Sammle Debug-Informationen
    const debugInfo = this.collectDebugInfo();
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      debugInfo: debugInfo
    });
    
    // Log für Server-seitiges Debugging
    console.error('[EnhancedErrorBoundary] Error caught:', {
      error: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      debug: debugInfo
    });
  }
  
  performAutoRecovery = async () => {
    try {
      // Service Worker Caches löschen
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Service Worker deregistrieren
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          await reg.unregister();
        }
      }
      
      // Hard reload
      window.location.href = window.location.pathname + '?recovered=' + Date.now();
    } catch (e) {
      console.error('[ErrorBoundary] Auto-recovery failed:', e);
      // Fallback: normaler Reload
      window.location.reload();
    }
  };
  
  collectDebugInfo = () => {
    const nav = navigator;
    const loc = window.location;
    
    // Cache-Status prüfen
    let cacheStatus = 'unknown';
    try {
      if ('caches' in window) {
        cacheStatus = 'available';
      }
    } catch (e) {
      cacheStatus = 'blocked';
    }
    
    // Service Worker Status
    let swStatus = 'not-supported';
    if ('serviceWorker' in navigator) {
      if (navigator.serviceWorker.controller) {
        swStatus = 'active';
      } else {
        swStatus = 'registered-no-controller';
      }
    }
    
    return {
      // Browser Info
      userAgent: nav.userAgent,
      platform: nav.platform,
      language: nav.language,
      cookiesEnabled: nav.cookieEnabled,
      onLine: nav.onLine,
      
      // Route Info
      currentPath: loc.pathname,
      fullUrl: loc.href,
      search: loc.search,
      hash: loc.hash,
      
      // Build Info
      buildVersion: BUILD_VERSION,
      reactVersion: React.version,
      
      // Cache Info
      cacheStatus: cacheStatus,
      serviceWorkerStatus: swStatus,
      
      // Timestamp
      timestamp: new Date().toISOString(),
      
      // Storage Status
      localStorage: this.checkStorage('localStorage'),
      sessionStorage: this.checkStorage('sessionStorage')
    };
  };
  
  checkStorage = (type) => {
    try {
      const storage = window[type];
      const testKey = '__storage_test__';
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      return 'available';
    } catch (e) {
      return 'blocked';
    }
  };

  handleReload = () => {
    // Cache löschen und zur Login-Seite
    try {
      localStorage.clear();
      sessionStorage.clear();
      
      // Service Worker Cache löschen
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
      
      // Service Worker deregistrieren
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(reg => reg.unregister());
        });
      }
    } catch (e) {
      console.error('Could not clear cache:', e);
    }
    
    // Hard reload zur Login-Seite
    window.location.href = '/login?cache_cleared=1';
  };

  handleRefresh = () => {
    // Force reload ohne Cache
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, debugInfo } = this.state;
      const errorMessage = error?.message || 'Unbekannter Fehler';
      
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-lg w-full">
            {/* Error Icon */}
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-semibold text-slate-900 mb-2 text-center">
              Ein Fehler ist aufgetreten
            </h2>
            <p className="text-slate-500 mb-4 text-sm text-center">
              Die Anwendung konnte nicht geladen werden.
            </p>
            
            {/* Error Message */}
            <div className="mb-4 p-3 bg-red-50 rounded-lg">
              <p className="text-xs text-red-700 font-mono break-all">
                {errorMessage}
              </p>
            </div>
            
            {/* Technical Details (Expanded) */}
            <details className="mb-4">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 font-medium">
                Technische Details anzeigen
              </summary>
              <div className="mt-3 space-y-3 text-xs">
                {/* Debug Info Table */}
                {debugInfo && (
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-1">
                      <span className="text-slate-500">Browser:</span>
                      <span className="text-slate-700 font-mono truncate" title={debugInfo.userAgent}>
                        {this.getBrowserName(debugInfo.userAgent)}
                      </span>
                      
                      <span className="text-slate-500">Route:</span>
                      <span className="text-slate-700 font-mono">{debugInfo.currentPath}</span>
                      
                      <span className="text-slate-500">Build:</span>
                      <span className="text-slate-700 font-mono">{debugInfo.buildVersion}</span>
                      
                      <span className="text-slate-500">React:</span>
                      <span className="text-slate-700 font-mono">v{debugInfo.reactVersion}</span>
                      
                      <span className="text-slate-500">Service Worker:</span>
                      <span className="text-slate-700 font-mono">{debugInfo.serviceWorkerStatus}</span>
                      
                      <span className="text-slate-500">Cache:</span>
                      <span className="text-slate-700 font-mono">{debugInfo.cacheStatus}</span>
                      
                      <span className="text-slate-500">LocalStorage:</span>
                      <span className="text-slate-700 font-mono">{debugInfo.localStorage}</span>
                      
                      <span className="text-slate-500">Online:</span>
                      <span className="text-slate-700 font-mono">{debugInfo.onLine ? 'Ja' : 'Nein'}</span>
                      
                      <span className="text-slate-500">Zeit:</span>
                      <span className="text-slate-700 font-mono">{new Date(debugInfo.timestamp).toLocaleString('de-DE')}</span>
                    </div>
                    
                    {/* Full User-Agent */}
                    <div className="pt-2 border-t border-slate-200">
                      <span className="text-slate-500 block mb-1">User-Agent:</span>
                      <span className="text-slate-600 font-mono text-[10px] break-all block">
                        {debugInfo.userAgent}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Stack Trace */}
                <div>
                  <span className="text-slate-500 block mb-1">Stack Trace:</span>
                  <pre className="p-2 bg-slate-100 rounded text-[10px] text-slate-600 overflow-auto max-h-32 break-all whitespace-pre-wrap">
                    {error?.stack}
                  </pre>
                </div>
                
                {/* Component Stack */}
                {errorInfo?.componentStack && (
                  <div>
                    <span className="text-slate-500 block mb-1">Component Stack:</span>
                    <pre className="p-2 bg-slate-100 rounded text-[10px] text-slate-600 overflow-auto max-h-32 break-all whitespace-pre-wrap">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
            
            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={this.handleReload}
                className="w-full bg-[#0E4C92] text-white py-3 px-4 rounded-lg hover:bg-[#0A3A70] transition-colors text-sm font-medium"
                data-testid="error-clear-cache-btn"
              >
                Cache löschen & neu anmelden
              </button>
              <button
                onClick={this.handleRefresh}
                className="w-full bg-slate-100 text-slate-700 py-3 px-4 rounded-lg hover:bg-slate-200 transition-colors text-sm"
                data-testid="error-refresh-btn"
              >
                Seite neu laden
              </button>
            </div>
            
            {/* Version Footer */}
            <p className="text-[10px] text-slate-400 text-center mt-4">
              Version: {BUILD_VERSION}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
  
  getBrowserName = (userAgent) => {
    if (!userAgent) return 'Unbekannt';
    
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      const match = userAgent.match(/Chrome\/(\d+)/);
      return `Chrome ${match ? match[1] : ''}`;
    }
    if (userAgent.includes('Edg')) {
      const match = userAgent.match(/Edg\/(\d+)/);
      return `Edge ${match ? match[1] : ''}`;
    }
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      const match = userAgent.match(/Version\/(\d+)/);
      return `Safari ${match ? match[1] : ''}`;
    }
    if (userAgent.includes('Firefox')) {
      const match = userAgent.match(/Firefox\/(\d+)/);
      return `Firefox ${match ? match[1] : ''}`;
    }
    
    // Mobile detection
    if (userAgent.includes('Mobile')) {
      return 'Mobile Browser';
    }
    
    return 'Browser';
  };
}

export default EnhancedErrorBoundary;
